// ─── Rattachements ESTIMÉ ↔ RÉEL : calculs purs ───────────────────
// Le budget estime, le compte constate, le suivi montre le delta. Ce module
// tient la charnière : QUI honore QUOI, et pour COMBIEN.
//
// Modèle n↔n avec répartition (décidé avec Cédric le 10/08) :
//   • une ligne prévue peut être honorée par PLUSIEURS opérations réelles
//     — « Courses 400 € » reçoit huit tickets ;
//   • une opération réelle peut couvrir PLUSIEURS lignes prévues, chacune
//     pour une PART de son montant — un prélèvement unique qui règle deux
//     échéances.
// Le 1 pour 1 reste le cas courant ; c'est simplement le cas particulier où
// une opération n'a qu'un rattachement, portant tout son montant.
//
//   rattachements/{id} { espaceId, depenseId, ligneId, mois: 'AAAA-MM',
//                        montant, origine: 'manuel'|'propose',
//                        creePar, creeLe }
//
// ⚠️ `origine: 'propose'` ne signifie PAS « appliqué automatiquement ». Rien
// n'est écrit sans l'accord de l'utilisateur (règle générale de l'app, la même
// que pour les virements internes) : le champ ne sert qu'à distinguer, plus
// tard, ce qui a été suggéré de ce qui a été saisi de zéro.
//
// Aucun accès Firestore ni React : ce fichier est testable tel quel.

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;
const abs = (x) => Math.abs(Number(x) || 0);

// Tolérance de comparaison monétaire : un demi-centime. En deçà, deux montants
// sont « le même » — sans quoi 0.1 + 0.2 déclarerait un écart.
export const EPSILON = 0.005;

// ── Lecture ──────────────────────────────────────────────────────
export const rattachementsDeDepense = (rattachements = [], depenseId) =>
  (depenseId ? rattachements.filter(r => r && r.depenseId === depenseId) : []);

// Ceux qui visent une OCCURRENCE précise, c'est-à-dire une ligne ET un mois :
// la même ligne existe tous les mois, c'est le couple qui identifie la cible.
export const rattachementsDOccurrence = (rattachements = [], ligneId, mois) =>
  (ligneId ? rattachements.filter(r => r && r.ligneId === ligneId && r.mois === mois) : []);

// Les rattachements d'un mois, chacun accompagné de SON opération — ce que la
// feuille de rattachement montre en tête (Cédric, 13/08) : sans ça, on ajoute
// un complément sans voir ce qu'il complète.
// Un rattachement dont l'opération a disparu est CONSERVÉ, avec `operation`
// nulle : le masquer ferait mentir le total affiché juste au-dessus.
export const rattachementsAvecOperation = (rattachements = [], operations = [], ligneId, mois) =>
  rattachementsDOccurrence(rattachements, ligneId, mois)
    .map(r => ({ ...r, operation: operations.find(o => o?.id === r.depenseId) || null }));

// « Ce mois ne recevra plus rien. » L'annotation fait foi ; son absence veut
// dire que l'occurrence est encore ouverte.
export const estSoldee = (exceptions = [], ligneId, mois) =>
  exceptions.some(e => e && e.ligneId === ligneId && e.mois === mois && e.statut === 'soldee');

// ── Répartition d'UNE opération réelle ───────────────────────────
// `reste` est ce qui n'est affecté à aucune ligne : c'est la part IMPRÉVUE de
// l'opération. Une opération peut donc être partiellement prévue — cas que le
// modèle précédent (un seul `ligneId`, sans montant) ne savait pas exprimer.
export function repartitionDepense(depense, rattachements = []) {
  const total = abs(depense?.montant);
  const liens = rattachementsDeDepense(rattachements, depense?.id);
  const affecte = cent(liens.reduce((s, r) => s + abs(r.montant), 0));
  return {
    total: cent(total),
    affecte,
    reste: cent(total - affecte),
    nb: liens.length,
    complet: Math.abs(total - affecte) < EPSILON,
    depasse: affecte - total > EPSILON,      // à refuser à l'écriture
  };
}

// Une opération est-elle encore (au moins en partie) imprévue ?
export const estImprevue = (depense, rattachements = []) =>
  repartitionDepense(depense, rattachements).reste > EPSILON;

// ── Réel constaté sur une occurrence ─────────────────────────────
// Somme des PARTS qui lui sont affectées — et non plus la somme des montants
// entiers des opérations rattachées.
export function reelDOccurrence(rattachements = [], ligneId, mois) {
  const liens = rattachementsDOccurrence(rattachements, ligneId, mois);
  return { reel: cent(liens.reduce((s, r) => s + abs(r.montant), 0)), nb: liens.length };
}

// Index { 'ligneId|mois' → { reel, nb } } : un seul passage sur la liste, pour
// que la vue mensuelle n'ait pas à refiltrer ligne par ligne.
export function indexerReel(rattachements = []) {
  const m = new Map();
  for (const r of rattachements) {
    if (!r?.ligneId || !r.mois) continue;
    const cle = `${r.ligneId}|${r.mois}`;
    const e = m.get(cle) || { reel: 0, nb: 0 };
    m.set(cle, { reel: cent(e.reel + abs(r.montant)), nb: e.nb + 1 });
  }
  return m;
}

// ── Écriture : ce qu'on s'autorise ───────────────────────────────
// Refuse ce qui produirait un état incohérent. Renvoie un motif en clair : il
// sera affiché tel quel, une écriture refusée en silence est pire que pas de
// contrôle du tout.
export function verifierRattachement({ depense, montant, rattachements = [], ligneId, mois, ignorerId = null }) {
  if (!depense?.id) return { ok: false, motif: 'operation' };
  if (!ligneId || !mois) return { ok: false, motif: 'cible' };
  const m = abs(montant);
  if (m < EPSILON) return { ok: false, motif: 'montantNul' };

  const autres = rattachementsDeDepense(rattachements, depense.id).filter(r => r.id !== ignorerId);
  const dejaAffecte = autres.reduce((s, r) => s + abs(r.montant), 0);
  if (dejaAffecte + m - abs(depense.montant) > EPSILON) return { ok: false, motif: 'depassement' };

  // Deux parts sur la MÊME occurrence : c'est une seule part à corriger, pas
  // deux lignes à empiler — sinon le total est juste mais la liste ment.
  if (autres.some(r => r.ligneId === ligneId && r.mois === mois)) return { ok: false, motif: 'doublon' };
  return { ok: true, motif: '' };
}

// Montant proposé par défaut : tout le reste à rattacher, borné au prévu de
// l'occurrence quand il est connu. Couvre le 1 pour 1 sans rien saisir, et
// amorce correctement le 1 pour n.
// Regle de Cedric (15/08) : 100 % de l'operation, ou 100 % de ce qui lui
// RESTE. Plus de bornage par le prevu de l'occurrence : c'est l'utilisateur
// qui ajuste, pas l'ecran qui decide. Le parametre `prevuOccurrence` est
// conserve pour ne pas casser les appelants, mais il ne borne plus rien.
export function montantParDefaut(depense, rattachements = [], prevuOccurrence = null) {
  const { reste } = repartitionDepense(depense, rattachements);
  return cent(reste);
}

// ── Répartition en POURCENTAGE (spéc. du 12/08, §10) ─────────────
// Décision de Cédric : « le % est purement de l'affichage, on peut donner le
// montant et considérer une division du montant ». On garde donc le MONTANT
// en base — l'invariant « somme des parts = montant de l'opération » reste
// vrai par construction, au lieu d'être un contrôle d'arrondi à faire.

// Le pourcentage qu'une part représente. Vue dérivée, jamais stockée.
export const pourcentage = (montantPart, montantOperation) => {
  const total = abs(montantOperation);
  if (!total) return 0;
  return Math.round((abs(montantPart) / total) * 10000) / 100;   // 2 décimales
};

// Part minimale : en dessous d'un centime, la spec (§15) demande de REFUSER
// plutôt que de créer un rattachement invisible et inexploitable.
export const PART_MINIMALE = 0.01;

/**
 * Ce que prendrait une nouvelle ligne si elle sélectionnait cette opération.
 * Règle §10.2 :
 *   reste > 0  → la nouvelle prend tout le reste ;
 *   reste = 0  → toutes les parts existantes sont divisées par deux, et la
 *                nouvelle prend la moitié de l'opération.
 *
 * Renvoie { montant, ajustements: [{ id, montant }], possible, motif }.
 * `ajustements` liste les rattachements des AUTRES lignes à réécrire — c'est
 * ce que la spec assume au §10.6 : la division modifie le réel d'autres
 * prévisions, sans dialogue bloquant mais avec un compte rendu.
 */
export function calculerPart(depense, rattachements = [], ignorerLigneId = null) {
  const total = abs(depense?.montant);
  if (!total) return { montant: 0, ajustements: [], possible: false, motif: 'montantNul' };

  const liens = rattachementsDeDepense(rattachements, depense?.id)
    .filter(r => r.ligneId !== ignorerLigneId);
  const affecte = cent(liens.reduce((s, r) => s + abs(r.montant), 0));
  const reste = cent(total - affecte);

  if (reste > EPSILON) {
    if (reste < PART_MINIMALE) return { montant: 0, ajustements: [], possible: false, motif: 'partTropPetite' };
    return { montant: reste, ajustements: [], possible: true, motif: '' };
  }

  // Plus rien de libre : on divise l'existant par deux.
  // ⚠️ Le seuil se teste AVANT l'arrondi : `cent(0.005)` vaut 0,01, ce qui
  // ferait passer une part réellement inférieure au centime. C'est la valeur
  // exacte qui décide, pas son affichage.
  if (total / 2 < PART_MINIMALE) return { montant: 0, ajustements: [], possible: false, motif: 'partTropPetite' };
  const moitie = cent(total / 2);

  const ajustements = liens.map(r => ({ id: r.id, ligneId: r.ligneId, montant: cent(abs(r.montant) / 2) }));
  // Le centime résiduel va à la PLUS GROSSE part (§10.4) : sans ça, la somme
  // des parts pourrait ne pas retomber exactement sur le montant.
  const sommeAjustee = cent(ajustements.reduce((s, a) => s + a.montant, 0));
  const ecart = cent(total - sommeAjustee - moitie);
  if (Math.abs(ecart) >= 0.01 && ajustements.length) {
    const plusGrosse = ajustements.reduce((m, a) => (a.montant > m.montant ? a : m), ajustements[0]);
    plusGrosse.montant = cent(plusGrosse.montant + ecart);
  }
  return { montant: moitie, ajustements, possible: true, motif: '' };
}

// Contrôle de la spec (§10.4) : la somme des parts doit retomber EXACTEMENT
// sur le montant de l'opération. Sert de garde-fou avant écriture.
export const repartitionExacte = (depense, parts = []) =>
  Math.abs(cent(parts.reduce((s, p) => s + abs(p.montant), 0)) - abs(depense?.montant)) < 0.005;
